import React, { useEffect, useState, useCallback } from 'react';

import { makeStyles } from '@material-ui/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import { styled, Typography } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';

import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';

import ToggleButton from '../../components/toggleButton';
import VariableTextField from '../../components/variableTextField';
import { useAPI } from '../../utils/api';
import VariableSelector from '../../components/variableSelector';
import { MODE } from '../../utils/mode';
import { useDialog } from '../../utils/dialog';

const useStyles = makeStyles({
  root: {
    '&:last-of-type td': {
      borderBottom: 'none',
    },
  },
});

const CellItemContainer = styled(Grid)({
  textAlign: 'center',
  borderLeft: '1px solid',
});

const CellItemGrid = styled(Grid)({
  padding: 6,
  borderBottom: '1px solid',
  borderRight: '1px solid',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflowWrap: 'anywhere',
});

const columns = [
  { id: 'order', label: '順序', minWidth: '80px' },
  { id: 'newsType', label: '類型', minWidth: '100px' },
  { id: 'content', label: '內容', minWidth: '200px', maxWidth: '300px' },
  { id: 'display', label: '狀態', minWidth: '100px' },
];

const createData = ({ id, order, type, newsType, content, display }) => {
  return { id, order, type, newsType, content, display };
};

const NewsConfigPage = () => {
  const classes = useStyles();
  const api = useAPI();
  const dialog = useDialog();

  const [isUnmounted, setIsUnmounted] = useState(false);
  const [mode, setMode] = useState(MODE.VIEW);
  const [newsTypes, setNewsTypes] = useState([]);
  const [news, setNews] = useState([]);
  const [shouldPageUpdated, setShouldPageUpdated] = useState(false);

  const onSubmit = (event) => {
    event.preventDefault();

    if (mode === MODE.EDIT) {
      const elements = event.target.querySelectorAll('td');
      const newNews = JSON.parse(JSON.stringify(news));

      for (const element of elements) {
        if (element.offsetHeight > 0) {
          for (const row of newNews) {
            const newType = element.querySelector(
              `input[name="newsType-${row.id}"]`
            );
            if (newType) row.newsType = newType.value;

            const newContent = element.querySelector(
              `input[name="content-${row.id}"]`
            );
            if (newContent) {
              if (newContent.value.length > 100) {
                dialog.openAlert({ message: '內容最多100個中英文數字' });
                return;
              }
              row.content = newContent.value;
            }

            const newDisplay = element.querySelector(
              `input[name="display-${row.id}"]`
            );
            if (newDisplay)
              row.display = newDisplay.checked
                ? newDisplay.value === 'display'
                : false;
          }
        }
      }

      const data = newNews;
      setMode(MODE.VIEW);

      api
        .updateNewsConfig(data)
        .then((isSuccess) => {
          if (isSuccess) getNewsConfig();
        })
        .catch(() => {});

      return;
    } else setMode(MODE.EDIT);
  };

  const getNewsConfig = useCallback(() => {
    api
      .getNewsConfig()
      .then((result) => {
        if (!isUnmounted) {
          const sliced = result.slice(0, 10);

          const rows = [];
          for (const row of sliced) {
            if (row.id <= 10) {
              rows.push(createData(row));
            }
          }

          setNews(rows);
        }
      })
      .catch(() => {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnmounted]);

  useEffect(() => {
    setShouldPageUpdated(false);
  }, [shouldPageUpdated]);

  useEffect(() => {
    setShouldPageUpdated(true);
  }, [news]);

  useEffect(() => {
    api
      .getNewsTypeDropDownList()
      .then((types) => {
        if (!isUnmounted) {
          setNewsTypes(types);
          getNewsConfig();
        }
      })
      .catch(() => {});

    return () => {
      setIsUnmounted(true);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Typography variant="body2" marginTop={2} marginBottom={2}>
        注意事項: 內容最多100個中英文數字。
      </Typography>
      {!shouldPageUpdated ? (
        <Box component="form" noValidate onSubmit={onSubmit}>
          <Paper
            sx={{
              width: '100%',
              overflow: 'hidden',
              padding: 1,
              marginBottom: 2,
            }}
            elevation={5}
          >
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow
                    sx={{
                      display: { xs: 'none', sm: 'table-row' },
                    }}
                  >
                    {news && columns
                      ? columns.map((column, index) => (
                          <TableCell
                            key={`${column.id}-${index}`}
                            align={column.align || 'center'}
                            sx={{
                              minWidth: column.minWidth || 'none',
                              maxWidth: column.maxWidth || '100%',
                              padding: 1,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {column.label}
                          </TableCell>
                        ))
                      : undefined}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {news
                    ? news.map((row) => {
                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            tabIndex={-1}
                            key={`${row.id}`}
                            className={classes.root}
                          >
                            {news && columns
                              ? columns.map((column) => {
                                  return (
                                    <TableCell
                                      key={`${column.id}-${row.id}`}
                                      align={column.align || 'center'}
                                      sx={{
                                        display: {
                                          xs: 'none',
                                          sm: 'table-cell',
                                        },
                                        padding: 1,
                                        minHeight: '0',
                                        maxWidth: column.maxWidth || '100%',
                                        whiteSpace:
                                          column.id === 'content'
                                            ? ''
                                            : 'nowrap',
                                      }}
                                    >
                                      {column.id === 'display' ? (
                                        <ToggleButton
                                          name={`${column.id}-${row.id}`}
                                          value="display"
                                          type="button"
                                          fullWidth
                                          variant="contained"
                                          checkedText="停止"
                                          uncheckedText="顯示"
                                          checkedColor="error"
                                          uncheckedColor="primary"
                                          checkedIcon={<VisibilityOffIcon />}
                                          unCheckedIcon={<VisibilityIcon />}
                                          initChecked={row.display}
                                          locked={mode !== MODE.EDIT}
                                        />
                                      ) : (
                                        <>
                                          {column.id === 'newsType' ? (
                                            <>
                                              <VariableSelector
                                                mode={mode}
                                                name={`${column.id}-${row.id}`}
                                                defaultValue={row.type}
                                                options={newsTypes}
                                                noLabel
                                                size="small"
                                              />
                                            </>
                                          ) : (
                                            <VariableTextField
                                              mode={
                                                column.id !== 'order' &&
                                                mode === MODE.EDIT
                                                  ? MODE.EDIT
                                                  : MODE.VIEW
                                              }
                                              noLabel
                                              name={`${column.id}-${row.id}`}
                                              text={row[column.id]}
                                              textAlign="center"
                                            />
                                          )}
                                        </>
                                      )}
                                    </TableCell>
                                  );
                                })
                              : undefined}

                            <TableCell
                              sx={{
                                display: {
                                  xs: 'block',
                                  sm: 'none',
                                  width: '100%',
                                },
                              }}
                            >
                              <CellItemContainer
                                container
                                borderTop="1px solid"
                                fontWeight="bold"
                              >
                                <CellItemGrid item xs={5}>
                                  順序
                                </CellItemGrid>
                                <CellItemGrid item xs={7}>
                                  類型
                                </CellItemGrid>
                              </CellItemContainer>
                              <CellItemContainer container>
                                <CellItemGrid item xs={5}>
                                  <VariableTextField
                                    mode={MODE.VIEW}
                                    noLabel
                                    name={`order-${row.id}`}
                                    text={row.order}
                                    textAlign="center"
                                  />
                                </CellItemGrid>
                                <CellItemGrid item xs={7}>
                                  <VariableSelector
                                    mode={mode}
                                    name={`newsType-${row.id}`}
                                    defaultValue={row.type}
                                    options={newsTypes}
                                    noLabel
                                    size="small"
                                  />
                                </CellItemGrid>
                              </CellItemContainer>
                              <CellItemContainer container fontWeight="bold">
                                <CellItemGrid item xs={5}>
                                  內容
                                </CellItemGrid>
                                <CellItemGrid item xs={7}>
                                  狀態
                                </CellItemGrid>
                              </CellItemContainer>
                              <CellItemContainer container>
                                <CellItemGrid item xs={12}>
                                  <VariableTextField
                                    mode={mode}
                                    noLabel
                                    name={`content-${row.id}`}
                                    text={row.content}
                                    textAlign="center"
                                  />
                                </CellItemGrid>
                              </CellItemContainer>
                              <CellItemContainer container fontWeight="bold">
                                <CellItemGrid item xs={12}>
                                  狀態
                                </CellItemGrid>
                              </CellItemContainer>
                              <CellItemContainer>
                                <Grid
                                  borderBottom="1px solid"
                                  borderRight="1px solid"
                                  justifyContent="center"
                                  margin="0"
                                  padding={1}
                                >
                                  <ToggleButton
                                    name={`display-${row.id}`}
                                    type="button"
                                    fullWidth
                                    value="display"
                                    initChecked={row.display}
                                    variant="contained"
                                    checkedText="停止"
                                    uncheckedText="顯示"
                                    checkedColor="error"
                                    uncheckedColor="primary"
                                    checkedIcon={<VisibilityOffIcon />}
                                    unCheckedIcon={<VisibilityIcon />}
                                    locked={mode !== MODE.EDIT}
                                    sx={{ maxWidth: '180px' }}
                                    size="small"
                                  />
                                  <Grid item xs={12}></Grid>
                                </Grid>
                              </CellItemContainer>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : undefined}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Grid
            container
            spacing={3}
            sx={{ margin: 'auto', width: '100%', justifyContent: 'center' }}
          >
            {
              <>
                <Button
                  type="submit"
                  variant="contained"
                  color={mode === MODE.EDIT ? 'success' : 'primary'}
                  sx={{ minWidth: '160px', maxWidth: '80%', color: 'white' }}
                  startIcon={mode === MODE.EDIT ? <SaveIcon /> : <EditIcon />}
                >
                  {mode === MODE.EDIT ? '儲存' : '編輯'}
                </Button>
              </>
            }
          </Grid>
        </Box>
      ) : undefined}
    </>
  );
};

export default NewsConfigPage;
